import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { ModalContext } from 'context/ModalContext';
import { Tabs, Descriptions, List, Avatar, Image, Badge, Button, Dropdown, Space, Card, Typography, Divider } from 'antd';
import * as Icon from 'react-feather';
import AddRecommendation from 'components/widgets/modals/AddRecommendation';
import { documents } from './../quotations/QuotationData';
import { quotationDetails } from 'services/apiSrv';

const { Title, Text } = Typography;

export default function View_Quotation() {
  const { id } = useParams();
  const { setModalShow, modalShow, modalClose } = useContext(ModalContext);
  const { TabPane } = Tabs;

  // State for dynamic fetching
  const [quotationData, setQuotationData] = React.useState({
    quotation_number: '',
    guest_client_quotation_ref: '',
    client_names: '',
    client_email: '', // Added new field for email
    category: '',
    status: '',
    gender: '',
    city: '',
    registration_date: '',
    address: '',
    avatar: '',
    currency: '',
    premium_total: 0,
    base_premium: 0,
    payment_status: '',
    cover: {
      type: '',
      period: 0
    },
    risks: [],
    underwriter: {
      name: '',
      email: '',
      contacts: []
    },
    intermediary: {
      name: '',
      agency: '',
      email: '',
      contact: ''
    }
  });
  const [fetchLoading, setFetchLoading] = React.useState(true);
  const [fetchError, setFetchError] = React.useState(null);

  // Fetch quotation details
  React.useEffect(() => {
    console.log("🔎 Fetching quotation details for ID:", id);

    quotationDetails(id)
      .then(res => {
        console.log(" Raw API response:", res);

        const api = res?.data ?? res;
        console.log(" Processed API object:", api);

        const item = api[0];     
        console.log(" Extracted item:", item);

        if (!item) {
          throw new Error("No quotation found in response.");
        }

        const fullName = [
          item?.clientDetails?.names?.fname,
          item?.clientDetails?.names?.lname
        ].filter(Boolean).join(" ");

        setQuotationData({
          quotation_number: item?.systemQuotationRef ?? '',
          guest_client_quotation_ref: item?.systemQuotationRef ?? '',
          client_names: fullName || item?.clientDetails?.email || '',
          client_email: item?.clientDetails?.email ?? '', // Set the email field
          category: item?.insuranceClassRef ?? '',
          status: item?.quotationStatus ?? '',
          gender: item?.clientDetails?.kyc?.gender ?? '',
          city: item?.clientDetails?.kyc?.city ?? '',
          registration_date: item?.createdAt
            ? new Date(item.createdAt).toLocaleString()
            : '',
          address:
            item?.clientDetails?.kyc?.address
            ?? item?.intermediary_details?.address
            ?? '',
          avatar: item?.clientDetails?.avatar ?? '',
          currency: item?.quotationCurrency ?? '',
          premium_total: item?.quotationPremium?.totalPremium ?? 0,
          base_premium: item?.quotationPremium?.basePremium ?? 0,
          payment_status: item?.quotationPayment?.paymentStatus ?? '',
          cover: {
            type: item?.quotationCoverDetails?.coverType ?? '',
            period: item?.quotationCoverDetails?.coverPeriod ?? 0
          },
          risks: (item?.risks || []).map(r => ({
            type: r?.riskType ?? '',
            ref: r?.riskRef ?? '',
            value: r?.value ?? 0
          })),
          underwriter: {
            name: item?.underwriter_details?.name ?? '',
            email: item?.underwriter_details?.emails?.[0] ?? '',
            contacts: item?.underwriter_details?.contacts ?? []
          },
          intermediary: {
            name: item?.intermediary_details?.names ?? '',
            agency: item?.intermediary_details?.kyc?.[0]?.intermediary_names ?? '',
            email: item?.intermediary_details?.email ?? '',
            contact: item?.intermediary_details?.contact ?? ''
          }
        });

        setFetchLoading(false);
        console.log("🎉 QuotationData set successfully");
      })
      .catch(err => {
        console.error("❌ Error fetching quotation details:", err);
        setFetchError("Failed to load quotation details.");
        setFetchLoading(false);
      });
  }, [id]);

  // Status color mapping
  const getStatusColor = (status) => {
    const statusColors = {
      'active': '#52c41a',
      'pending': '#faad14',
      'completed': '#1890ff',
      'cancelled': '#ff4d4f',
      'draft': '#722ed1'
    };
    return statusColors[status?.toLowerCase()] || '#52c41a';
  };

  // Action menu items
  const actionItems = [
    {
      key: '1',
      label: (
        <Space>
          <Icon.CheckSquare size={16} color="#1890ff" />
          <span>Recommend a product(s)</span>
        </Space>
      ),
      onClick: () => setModalShow(true)
    },
    {
      key: '2',
      label: (
        <Space>
          <Icon.Info size={16} color="#faad14" />
          <span>Request Additional Info</span>
        </Space>
      ),
    },
    {
      key: '3',
      label: (
        <Space>
          <Icon.Send size={16} color="#52c41a" />
          <span>Send to Client</span>
        </Space>
      ),
    }
  ];

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f8fafc',
      padding: '24px'
    }}>
      {fetchLoading ? (
        <p>Loading quotation details...</p>
      ) : fetchError ? (
        <p>{fetchError}</p>
      ) : (
        <>
          {/* Header Section */}
          <Card 
            style={{ 
              marginBottom: '24px',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: 'none'
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '14px' }}>Quotation Number</Text>
                  <Title level={4} style={{ margin: 0, color: '#1890ff', fontWeight: 600 }}>
                    {quotationData.guest_client_quotation_ref}
                  </Title>
                </div>
                <Badge 
                  color={getStatusColor(quotationData.status)}
                  text={
                    <span style={{ 
                      color: getStatusColor(quotationData.status),
                      fontWeight: 500,
                      textTransform: 'capitalize'
                    }}>
                      <Icon.Clock size={12} style={{ marginRight: '4px' }} />
                      {quotationData.status}
                    </span>
                  }
                />
              </div>
              
              <div style={{ display: 'flex', gap: '32px', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '14px' }}>Category</Text>
                  <div style={{ color: '#1890ff', fontWeight: 500 }}>{quotationData.category}</div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: '14px' }}>Client Name</Text>
                  <div style={{ color: '#1890ff', fontWeight: 500 }}>{quotationData.client_names}</div>
                </div>
              </div>

              <Dropdown
                menu={{ items: actionItems }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Button 
                  type="primary" 
                  size="large"
                  style={{
                    borderRadius: '8px',
                    backgroundColor: '#1890ff',
                    border: 'none',
                    fontWeight: 500
                  }}
                >
                  Actions <Icon.ChevronDown size={16} style={{ marginLeft: '4px' }} />
                </Button>
              </Dropdown>
            </div>
          </Card>

          {/* Tabs Section */}
          <Tabs 
            type="card" 
            size="large"
            style={{
              '& .ant-tabs-card .ant-tabs-content': {
                marginTop: '16px'
              }
            }}
            items={[
              {
                key: '1',
                label: (
                  <Space>
                    <Icon.User size={16} />
                    Client Details
                  </Space>
                ),
                children: (
                  <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                    {/* Client Profile Card */}
                    <Card 
                      style={{ 
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                        border: 'none',
                        background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                        color: 'white',
                        minWidth: '320px',
                        flex: '0 0 auto'
                      }}
                    >
                      <div style={{ textAlign: 'center', padding: '8px 0' }}>
                        {/* Enhanced Avatar Section */}
                        <div style={{ 
                          position: 'relative',
                          display: 'inline-block',
                          marginBottom: '20px'
                        }}>
                          <div style={{ 
                            width: '140px', 
                            height: '140px', 
                            borderRadius: '50%', 
                            overflow: 'hidden',
                            margin: '0 auto',
                            border: '5px solid rgba(255,255,255,0.3)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                            position: 'relative'
                          }}>
                            <Image 
                              width={140} 
                              height={140}
                              src={quotationData.avatar} 
                              style={{ 
                                objectFit: 'cover',
                                filter: 'brightness(1.05) contrast(1.05)'
                              }}
                              fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTQwIiBoZWlnaHQ9IjE0MCIgdmlld0JveD0iMCAwIDE0MCAxNDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxNDAiIGhlaWdodD0iMTQwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik07MCA5MEM4MS4wNDU3IDkwIDkwIDgxLjA0NTcgOTAgNzBDOTAgNTguOTU0MyA4MS4wNDU3IDUwIDcwIDUwQzU4Ljk1NDMgNTAgNTAgNTguOTU0MyA1MCA3MEM1MCA4MS4wNDU3IDU4Ljk1NDMgOTAgNzAgOTBaIiBmaWxsPSIjQzRDNEM0Ii8+CjxwYXRoIGQ9Ik0zMCAxMTBDMzAgMTAwIDUwIDkwIDcwIDkwQzkwIDkwIDExMCAxMDAgMTEwIDExMFYxMjBIMzBWMTEwWiIgZmlsbD0iI0M0QzRDNCIvPgo8L3N2Zz4K"
                            />
                          </div>
                          {/* Status indicator */}
                          <div style={{
                            position: 'absolute',
                            bottom: '10px',
                            right: '10px',
                            width: '24px',
                            height: '24px',
                            backgroundColor: getStatusColor(quotationData.status),
                            border: '3px solid white',
                            borderRadius: '50%',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                          }} />
                        </div>
                        
                        {/* Client Name */}
                        <Title level={3} style={{ 
                          margin: '0 0 8px 0', 
                          color: 'white',
                          fontWeight: 600,
                          textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          {quotationData.client_names || 'Client Name'}
                        </Title>
                        
                        {/* Quick Info Pills */}
                        <div style={{ 
                          display: 'flex', 
                          gap: '8px', 
                          justifyContent: 'center',
                          flexWrap: 'wrap',
                          marginBottom: '20px'
                        }}>
                          <div style={{
                            background: 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 500
                          }}>
                            <Icon.MapPin size={12} style={{ marginRight: '4px' }} />
                            {quotationData.city || 'City'}
                          </div>
                          <div style={{
                            background: 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 500
                          }}>
                            <Icon.Calendar size={12} style={{ marginRight: '4px' }} />
                            {quotationData.registration_date || 'Date'}
                          </div>
                        </div>

                        {/* Contact Actions */}
                        <div style={{ 
                          display: 'flex', 
                          gap: '12px', 
                          justifyContent: 'center' 
                        }}>
                          <Button 
                            type="text" 
                            shape="circle"
                            icon={<Icon.Phone size={18} />}
                            style={{
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              border: 'none',
                              color: 'white',
                              width: '44px',
                              height: '44px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          />
                          <Button 
                            type="text" 
                            shape="circle"
                            icon={<Icon.Mail size={18} />}
                            style={{
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              border: 'none',
                              color: 'white',
                              width: '44px',
                              height: '44px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          />
                          <Button 
                            type="text" 
                            shape="circle"
                            icon={<Icon.MessageCircle size={18} />}
                            style={{
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              border: 'none',
                              color: 'white',
                              width: '44px',
                              height: '44px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          />
                        </div>
                      </div>
                    </Card>

                    {/* Client Information Card */}
                    <Card 
                      style={{ 
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                        border: 'none',
                        flex: '1 1 400px',
                        minWidth: '400px'
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '28px'
                      }}>
                        <Title level={3} style={{ 
                          margin: 0, 
                          color: '#1f2937',
                          fontWeight: 600
                        }}>
                          Client Information
                        </Title>
                      </div>
                      
                      {/* Enhanced Descriptions */}
                      <div style={{ display: 'grid', gap: '24px' }}>
                        {/* Personal Information Section */}
                        <div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '16px',
                            paddingBottom: '8px',
                            borderBottom: '2px solid #e5e7eb'
                          }}>
                            <Icon.User size={18} color="#6366f1" />
                            <Text strong style={{ fontSize: '16px', color: '#374151' }}>
                              Personal Information
                            </Text>
                          </div>
                          
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '20px'
                          }}>
                            <div style={{ 
                              padding: '16px',
                              backgroundColor: '#f8fafc',
                              borderRadius: '12px',
                              border: '1px solid #e2e8f0'
                            }}>
                              <Text type="secondary" style={{ 
                                fontSize: '12px', 
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontWeight: 600
                              }}>
                                Client Name
                              </Text>
                              <div style={{ 
                                fontSize: '15px', 
                                fontWeight: 500,
                                color: '#1f2937',
                                marginTop: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <Icon.User size={14} color="#3b82f6" />
                                {quotationData.client_names || 'Not specified'}
                              </div>
                            </div>
                            <div style={{ 
                              padding: '16px',
                              backgroundColor: '#f8fafc',
                              borderRadius: '12px',
                              border: '1px solid #e2e8f0'
                            }}>
                              <Text type="secondary" style={{ 
                                fontSize: '12px', 
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontWeight: 600
                              }}>
                                Email
                              </Text>
                              <div style={{ 
                                fontSize: '15px', 
                                fontWeight: 500,
                                color: '#1f2937',
                                marginTop: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <Icon.Mail size={14} color="#10b981" />
                                {quotationData.client_email || 'Not specified'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                )
              },
              {
                key: '2',
                label: (
                  <Space>
                    <Icon.FileText size={16} />
                    Request Details
                  </Space>
                ),
                children: (
                  <Card 
                    style={{ 
                      borderRadius: '12px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      border: 'none',
                      minHeight: '300px'
                    }}
                  >
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '40px',
                      backgroundColor: '#f1f5f9',
                      borderRadius: '8px'
                    }}>
                      <Icon.FileText size={48} color="#1890ff" style={{ marginBottom: '16px' }} />
                      <Title level={4} style={{ color: '#595959' }}>Request Details</Title>
                      <Text type="secondary">Request details will appear here</Text>
                    </div>
                  </Card>
                )
              },
              {
                key: '3',
                label: (
                  <Space>
                    <Icon.Shield size={16} />
                    Risk Details
                  </Space>
                ),
                children: (
                  <div>
                    <div style={{ marginBottom: '16px', textAlign: 'right' }}>
                      <Button 
                        type="primary" 
                        icon={<Icon.Plus size={16} />}
                        style={{
                          borderRadius: '6px',
                          backgroundColor: '#ef4444',
                          border: 'none'
                        }}
                      >
                        Add Risk
                      </Button>
                    </div>
                    <Card 
                      style={{ 
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: 'none'
                      }}
                    >
                      <div style={{ display: 'flex', gap: '24px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            padding: '24px',
                            backgroundColor: '#fef3c7',
                            borderRadius: '8px'
                          }}>
                            <Title level={4} style={{ color: '#595959', marginBottom: '24px' }}>
                              Risk Analysis
                            </Title>
                            <List
                              dataSource={quotationData.risks}
                              renderItem={(risk, index) => (
                                <List.Item style={{ padding: '12px 0' }}>
                                  <List.Item.Meta
                                    avatar={
                                      <Avatar 
                                        style={{ 
                                          backgroundColor: '#fef3c7',
                                          border: '1px solid #fa8c16'
                                        }}
                                        icon={<Icon.Shield size={16} color="#fa8c16" />}
                                      />
                                    }
                                    title={
                                      <Text strong style={{ color: '#1f2937' }}>
                                        {risk.type || 'Risk Type'}
                                      </Text>
                                    }
                                    description={
                                      <div>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                          Reference: {risk.ref || 'N/A'}
                                        </Text>
                                        <br />
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                          Value: {risk.value ? `${quotationData.currency} ${risk.value.toFixed(2)}` : 'N/A'}
                                        </Text>
                                      </div>
                                    }
                                  />
                                </List.Item>
                              )}
                            />
                            {quotationData.risks.length === 0 && (
                              <div style={{ textAlign: 'center', padding: '24px' }}>
                                <Icon.Shield size={48} color="#fa8c16" style={{ marginBottom: '16px' }} />
                                <Text type="secondary">No risk details available</Text>
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ minWidth: '300px' }}>
                          <Card 
                            size="small" 
                            title={
                              <Space>
                                <Icon.Paperclip size={16} />
                                <Text strong>Attached Documents</Text>
                              </Space>
                            }
                            style={{ 
                              borderRadius: '8px',
                              border: '1px solid #f0f0f0'
                            }}
                          >
                            <List
                              itemLayout="horizontal"
                              dataSource={documents}
                              renderItem={(item) => (
                                <List.Item style={{ padding: '8px 0' }}>
                                  <List.Item.Meta
                                    avatar={
                                      <Avatar 
                                        style={{ 
                                          backgroundColor: '#e6f7ff',
                                          border: '1px solid #91d5ff'
                                        }}
                                        icon={<Icon.File size={16} color="#1890ff" />}
                                      />
                                    }
                                    title={
                                      <a 
                                        href="#" 
                                        style={{ 
                                          color: '#1890ff',
                                          fontWeight: 500,
                                          textDecoration: 'none'
                                        }}
                                      >
                                        {item.title}
                                      </a>
                                    }
                                    description={
                                      <Text type="secondary" style={{ fontSize: '12px' }}>
                                        Sample description
                                      </Text>
                                    }
                                  />
                                </List.Item>
                              )}
                            />
                          </Card>
                        </div>
                      </div>
                    </Card>
                  </div>
                )
              },
              {
                key: '4',
                label: (
                  <Space>
                    <Icon.FileText size={16} />
                    Quotation Details
                  </Space>
                ),
                children: (
                  <Card 
                    style={{ 
                      borderRadius: '12px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      border: 'none',
                      minHeight: '300px'
                    }}
                  >
                    <div style={{ 
                      padding: '24px',
                      backgroundColor: '#ecfdf5',
                      borderRadius: '8px'
                    }}>
                      <Title level={4} style={{ color: '#595959', marginBottom: '24px' }}>
                        Quotation Summary
                      </Title>
                      <div style={{ 
                        display: 'grid', 
                        gap: '24px'
                      }}>
                        {/* Premium Information */}
                        <div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '16px',
                            paddingBottom: '8px',
                            borderBottom: '2px solid #d1fae5'
                          }}>
                            <Icon.Briefcase size={18} color="#059669" />
                            <Text strong style={{ fontSize: '16px', color: '#374151' }}>
                              Premium Details
                            </Text>
                          </div>
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '16px'
                          }}>
                            <div style={{ 
                              padding: '16px',
                              backgroundColor: '#f0fdf4',
                              borderRadius: '12px',
                              border: '1px solid #d1fae5'
                            }}>
                              <Text type="secondary" style={{ 
                                fontSize: '12px', 
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontWeight: 600
                              }}>
                                Base Premium
                              </Text>
                              <div style={{ 
                                fontSize: '15px', 
                                fontWeight: 500,
                                color: '#1f2937',
                                marginTop: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <Icon.Briefcase size={14} color="#059669" />
                                {quotationData.currency} {quotationData.base_premium.toFixed(2)}
                              </div>
                            </div>
                            <div style={{ 
                              padding: '16px',
                              backgroundColor: '#f0fdf4',
                              borderRadius: '12px',
                              border: '1px solid #d1fae5'
                            }}>
                              <Text type="secondary" style={{ 
                                fontSize: '12px', 
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontWeight: 600
                              }}>
                                Total Premium
                              </Text>
                              <div style={{ 
                                fontSize: '15px', 
                                fontWeight: 500,
                                color: '#1f2937',
                                marginTop: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <Icon.Briefcase size={14} color="#059669" />
                                {quotationData.currency} {quotationData.premium_total.toFixed(2)}
                              </div>
                            </div>
                            <div style={{ 
                              padding: '16px',
                              backgroundColor: '#f0fdf4',
                              borderRadius: '12px',
                              border: '1px solid #d1fae5'
                            }}>
                              <Text type="secondary" style={{ 
                                fontSize: '12px', 
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontWeight: 600
                              }}>
                                Payment Status
                              </Text>
                              <div style={{ 
                                fontSize: '15px', 
                                fontWeight: 500,
                                color: '#1f2937',
                                marginTop: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <Icon.CheckCircle size={14} color="#059669" />
                                {quotationData.payment_status || 'Not specified'}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Cover Information */}
                        <div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '16px',
                            paddingBottom: '8px',
                            borderBottom: '2px solid #d1fae5'
                          }}>
                            <Icon.Shield size={18} color="#059669" />
                            <Text strong style={{ fontSize: '16px', color: '#374151' }}>
                              Cover Details
                            </Text>
                          </div>
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '16px'
                          }}>
                            <div style={{ 
                              padding: '16px',
                              backgroundColor: '#f0fdf4',
                              borderRadius: '12px',
                              border: '1px solid #d1fae5'
                            }}>
                              <Text type="secondary" style={{ 
                                fontSize: '12px', 
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontWeight: 600
                              }}>
                                Cover Type
                              </Text>
                              <div style={{ 
                                fontSize: '15px', 
                                fontWeight: 500,
                                color: '#1f2937',
                                marginTop: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <Icon.Shield size={14} color="#059669" />
                                {quotationData.cover.type || 'Not specified'}
                              </div>
                            </div>
                            <div style={{ 
                              padding: '16px',
                              backgroundColor: '#f0fdf4',
                              borderRadius: '12px',
                              border: '1px solid #d1fae5'
                            }}>
                              <Text type="secondary" style={{ 
                                fontSize: '12px', 
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontWeight: 600
                              }}>
                                Cover Period
                              </Text>
                              <div style={{ 
                                fontSize: '15px', 
                                fontWeight: 500,
                                color: '#1f2937',
                                marginTop: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <Icon.Calendar size={14} color="#059669" />
                                {quotationData.cover.period ? `${quotationData.cover.period} months` : 'Not specified'}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Underwriter Information */}
                        <div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '16px',
                            paddingBottom: '8px',
                            borderBottom: '2px solid #d1fae5'
                          }}>
                            <Icon.UserCheck size={18} color="#059669" />
                            <Text strong style={{ fontSize: '16px', color: '#374151' }}>
                              Underwriter Details
                            </Text>
                          </div>
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '16px'
                          }}>
                            <div style={{ 
                              padding: '16px',
                              backgroundColor: '#f0fdf4',
                              borderRadius: '12px',
                              border: '1px solid #d1fae5'
                            }}>
                              <Text type="secondary" style={{ 
                                fontSize: '12px', 
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontWeight: 600
                              }}>
                                Underwriter Name
                              </Text>
                              <div style={{ 
                                fontSize: '15px', 
                                fontWeight: 500,
                                color: '#1f2937',
                                marginTop: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <Icon.UserCheck size={14} color="#059669" />
                                {quotationData.underwriter.name || 'Not specified'}
                              </div>
                            </div>
                            <div style={{ 
                              padding: '16px',
                              backgroundColor: '#f0fdf4',
                              borderRadius: '12px',
                              border: '1px solid #d1fae5'
                            }}>
                              <Text type="secondary" style={{ 
                                fontSize: '12px', 
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontWeight: 600
                              }}>
                                Email
                              </Text>
                              <div style={{ 
                                fontSize: '15px', 
                                fontWeight: 500,
                                color: '#1f2937',
                                marginTop: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <Icon.Mail size={14} color="#059669" />
                                {quotationData.underwriter.email || 'Not specified'}
                              </div>
                            </div>
                            <div style={{ 
                              padding: '16px',
                              backgroundColor: '#f0fdf4',
                              borderRadius: '12px',
                              border: '1px solid #d1fae5'
                            }}>
                              <Text type="secondary" style={{ 
                                fontSize: '12px', 
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontWeight: 600
                              }}>
                                Contacts
                              </Text>
                              <div style={{ 
                                fontSize: '15px', 
                                fontWeight: 500,
                                color: '#1f2937',
                                marginTop: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <Icon.Phone size={14} color="#059669" />
                                {quotationData.underwriter.contacts.length > 0 
                                  ? quotationData.underwriter.contacts.join(', ') 
                                  : 'Not specified'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                )
              }
            ]}
          />
          
          <AddRecommendation
            show={modalShow}
            handleClose={modalClose}
            title="Add Recommendation"
            description="Add a new recommendation"
          />
        </>
      )}
    </div>
  );
}